import {Barcode, LogLevel, NfcCard, Occupancy} from "@variocube/app-api";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {AccessStatus, Availability, Booking, BookingOptions, Bookings, getOccupancyBooking} from "./booking";
import {BookingCodeView} from "./BookingCodeView";
import {BookingFlow} from "./BookingFlow";
import {BookingHome} from "./BookingHome";
import {ProlongBookingFlow} from "./ProlongBookingFlow";
import {
    classNames,
    createTheme,
    useBarcodeListener,
    useCardListener,
    useDeactivateListener,
    useOccupancies,
    usePlatform, useStorageItemBlob,
    useStorageItemValue
} from "@variocube/cube-ui";
import {BookingView} from "./BookingView";
import {BookingNotFoundView} from "./BookingNotFoundView";
import {themeOptions} from "@variocube/app-host/src/theme";
import {style} from "typestyle";
import {background} from "./background";

export function BookingApp() {
    const [bookingFlow, setBookingFlow] = useState(false);
    const [bookingCode, setBookingCode] = useState(false);
    const [tokenAccessStatus, setTokenAccessStatus] = useState(AccessStatus.None);
    const [codeAccessStatus, setCodeAccessStatus] = useState(AccessStatus.None);
    const [access, setAccess] = useState<Booking|string>();
    const [prolongBookingFlow, setProlongBookingFlow] = useState<Occupancy>();

    const platform = usePlatform();
    const bookings = useStorageItemValue<Bookings>("bookings");
    const occupancies = useOccupancies();

    const bookingOptions = useStorageItemValue<BookingOptions>("bookingOptions");
    const availability = useStorageItemValue<Availability>("availability");
    const onlineBookingUrl = useStorageItemValue<string>("onlineBookingUrl");

    const reset = useCallback(async () => {
        setBookingFlow(false);
        setBookingCode(false);
        setTokenAccessStatus(AccessStatus.None);
        setCodeAccessStatus(AccessStatus.None);
        setAccess(undefined);
    }, []);

    useDeactivateListener(reset);

    const handleBookingCode = useCallback(async (bookingCode: string) => {
        setCodeAccessStatus(AccessStatus.Pending);

        console.log("Looking up booking for booking code: ", bookingCode);

        console.log("Bookings", bookings);

        // There must be either a valid booking, or the booking code must be the access code of an occupancy.
        const booking = (bookings && bookings[bookingCode])
            ?? (occupancies.filter(occupancy => occupancy.accessCode == bookingCode).length > 0 ? bookingCode : undefined);

        console.log("Booking: ", booking);

        if (booking) {
            await platform.log(LogLevel.Audit, "Access granted for booking code", {bookingCode});
            setAccess(booking);
            setBookingCode(false);
            setCodeAccessStatus(AccessStatus.None);
        }
        else {
            await platform.log(LogLevel.Audit, "Access denied for booking code", {bookingCode});
            setCodeAccessStatus(AccessStatus.Failed);
        }

    }, [platform, bookings]);

    function findBookingByCodeOrKey(codeOrKey: string) {
        if (bookings && codeOrKey) {
            return Object.values(bookings)
                .filter(booking => codeOrKey == booking.bookingCode || codeOrKey == booking.bookingKey)
                .pop();
        }
    }

    function findCardBookingCode(cardUid: string) {
        const occupancy = occupancies.filter(occupancy => {
            const booking = getOccupancyBooking(occupancy);
            return booking && cardUid == booking.cardUid;
        }).pop();
        return occupancy?.accessCode;
    }


    async function handleToken(token: string) {
        if (token && !bookingFlow && !prolongBookingFlow && !access) {
            const booking = findCardBookingCode(token) || findBookingByCodeOrKey(token);

            if (booking) {
                await platform.log(LogLevel.Audit, "Access granted for token", {token});
                setAccess(booking);
            }
            else {
                await platform.log(LogLevel.Audit, "Access denied for token", {token});
                setTokenAccessStatus(AccessStatus.Failed);
            }
        }
    }

    useCardListener(async (card?: NfcCard) => {
        if (card) {
            await handleToken(card.uid);
        }
    });
    useBarcodeListener(async (code: Barcode) => {
        await handleToken(atob(code.code));
    })

    const handleBookingDone = useCallback((occupancy?: Occupancy) => {
        if (occupancy) {
            setAccess(occupancy.accessCode);
        }
        setBookingFlow(false);
    }, []);

    const handleProlong = useCallback((occupancy: Occupancy) => setProlongBookingFlow(occupancy), []);

    const handleProlongBookingDone = useCallback((newOccupancy?: Occupancy) => {
        if (newOccupancy) {
            // console.log("Booking was prolonged with new occupancy", newOccupancy); // somehow this console.log produces error
            setProlongBookingFlow(undefined);
            setAccess(newOccupancy.accessCode);
        }
        else {
            console.log("Booking was not prolonged.");
            setProlongBookingFlow(undefined);
        }
    }, []);

    const handleClearOut = useCallback(async (occupancy: Occupancy) => {
        try {
            await platform.openBoxLock(occupancy.boxNumber);
            await platform.endOccupancy(occupancy.uuid);
            setBookingFlow(false);
            setBookingCode(false);
            setProlongBookingFlow(undefined);
            setAccess(undefined);
        } catch (err) {
            console.error('Failed to open lock and end occupancy', err);
        }
    }, [])

    const handleBook = useCallback(() => setBookingFlow(true), []);
    const handleOpen = useCallback(() => setBookingCode(true), []);

    const {accessibleFrom = "00:00", accessibleUntil = "23:59", accessibility = {}} = bookingOptions ?? {};

    const currency = bookingOptions?.currency || "EUR";

    const colorPrimary = useStorageItemValue<string>("colorPrimary") ?? "#f18721"
    const colorChoice = useStorageItemValue<string>("colorChoice") ?? "#32849b"
    const customTheme = tenantTheme(colorPrimary, colorChoice);

    function renderView() {
        if (bookingFlow && bookingOptions && bookingOptions.enablePayment && availability) {
            return (
                <BookingFlow
                    onDone={handleBookingDone}
                    platform={platform}
                    enablePhone={bookingOptions.enablePhone}
                    enableEmail={bookingOptions.enableEmail}
                    enableDisplay={bookingOptions.enableDisplay}
                    plans={bookingOptions.plans}
                    onlineBookingUrl={onlineBookingUrl}
                    accessibleFrom={accessibleFrom}
                    accessibleUntil={accessibleUntil}
                    accessibility={accessibility}
                    availability={availability}
                    paymentOptions={bookingOptions.enablePayment}
                    termsUrl={bookingOptions.termsUrl}
                    privacyPolicyUrl={bookingOptions.privacyPolicyUrl}
                    currency={currency}
                    bookingUnit={bookingOptions.bookingUnit}
                    pricingMode={bookingOptions.pricingMode}
                    discounts={bookingOptions.discounts}
                    className={customTheme}
                />
            );
        }
        else if (bookingCode) {
            return (
                <BookingCodeView
                    onCancel={reset}
                    onBookingCode={handleBookingCode}
                    bookingCodeAccessStatus={codeAccessStatus}
                    className={customTheme}
                />
            );
        }
        else if (prolongBookingFlow && bookingOptions && bookingOptions.enablePayment) {
            return (
                <ProlongBookingFlow
                    plans={bookingOptions.plans}
                    paymentOptions={bookingOptions.enablePayment}
                    onDone={handleProlongBookingDone}
                    onClearOut={handleClearOut}
                    accessibleFrom={accessibleFrom}
                    accessibleUntil={accessibleUntil}
                    accessibility={accessibility}
                    occupancy={prolongBookingFlow}
                    currency={currency}
                    bookingUnit={bookingOptions.bookingUnit}
                    pricingMode={bookingOptions.pricingMode}
                    className={customTheme}
                />
            );
        }
        else if (access) {
            return (
                <BookingView
                    booking={access}
                    onDone={reset}
                    onProlong={handleProlong}
                    className={customTheme}
                />
            )
        }
        else if (tokenAccessStatus == AccessStatus.Failed) {
            return (
                <BookingNotFoundView
                    onClose={() => setTokenAccessStatus(AccessStatus.None)}
                    className={customTheme}
                />
            )
        }
        return (
            <BookingHome
                onBook={handleBook}
                onOpen={handleOpen}
                siteName={bookingOptions?.siteName}
                className={customTheme}
            />
        );
    }

    const backgroundImageBlob = useStorageItemBlob("backgroundImage");
    const backgroundImageUrl = useMemo(() => backgroundImageBlob && URL.createObjectURL(backgroundImageBlob), [backgroundImageBlob]);
    const customBackground = backgroundImageUrl && style({backgroundImage: `url("${backgroundImageUrl}")`});

    return (
        <div style={{ height: '100%' }} className={classNames(background, customBackground)}>
            {renderView()}
        </div>
    )
}

const tenantTheme = (colorPrimary: string, colorChoice: string) => createTheme({
    ...themeOptions,
    colorPrimary,
    colorChoice
});